// Collection engine shared by the Payments and QR Payments tabs.
//
// Window = the stall drive picked at the top (campus + dates). Dates are
// collection dates, not only booking dates: an August order whose balance
// came on 19 Sep belongs to the 18–21 Sep drive.
//
// Two separate questions, never mixed:
//   who booked the order      -> bookedBy
//   where that money sits now -> each payment line's holder / QR
// Cash stays with whoever took it (booking or delivery). Online has to land
// on a QR, and the QR name is only saved when Done is pressed in the app.

const IST_MS = 330 * 60 * 1000;

export const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

export function dayKey(v) {
    if (v == null || v === "") return "";
    const s = String(v);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return "";
    const ist = new Date(d.getTime() + IST_MS);
    return `${ist.getUTCFullYear()}-${String(ist.getUTCMonth() + 1).padStart(2, "0")}-${String(ist.getUTCDate()).padStart(2, "0")}`;
}

export function clockIST(v) {
    if (!v) return "";
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "";
    const ist = new Date(d.getTime() + IST_MS);
    return `${String(ist.getUTCHours()).padStart(2, "0")}:${String(ist.getUTCMinutes()).padStart(2, "0")}`;
}

const norm = (s) => String(s || "").trim().toLowerCase().replace(/\s+/g, " ");

function inWindow(day, from, to) {
    if (!from && !to) return true;
    if (!day) return false;
    if (from && day < from) return false;
    if (to && day > to) return false;
    return true;
}

const campusOk = (campus, want) => !want || (campus || "") === want;

export function advanceSplit(b) {
    const mode = String(b.payment_mode || "").toLowerCase();
    const adv = num(b.advance_amount);
    const cash = num(b.advance_cash);
    const online = num(b.advance_online);
    if (mode === "both" || (cash > 0 && online > 0)) return { cash, online };
    if (mode === "cash") return { cash: cash || adv, online: 0 };
    if (mode === "online" || mode === "upi" || mode === "qr") return { cash: 0, online: online || adv };
    if (cash || online) return { cash, online };
    return { cash: 0, online: 0 };
}

function logsOf(b) {
    const logs = b.delivery_log;
    if (!logs) return [];
    if (Array.isArray(logs)) return logs.filter((x) => x && typeof x === "object");
    if (typeof logs === "object") return [logs];
    return [];
}

function logSplit(lg) {
    let cash = num(lg.cash);
    let online = num(lg.online);
    const paid = num(lg.paid);
    const mode = String(lg.pay_mode || "").toLowerCase();
    if (cash === 0 && online === 0 && paid > 0) {
        if (mode === "cash") cash = paid;
        else if (mode === "online" || mode === "upi" || mode === "qr") online = paid;
    }
    return {
        cash, online, mode,
        by: lg.by_name || lg.by || "",
        at: lg.at || "",
        receipt: typeof lg.receipt === "string" ? lg.receipt : "",
        qrName: lg.qr_name || "",
        items: Array.isArray(lg.delivered) ? lg.delivered.length : 0,
    };
}

const dueOf = (b) => Math.max(0, num(b.total_amount) - num(b.advance_amount) - num(b.balance_paid));

function shotRef(v) {
    if (typeof v !== "string") return "";
    if (v.startsWith("media:") || v.startsWith("http")) return v.length < 400 ? v : "";
    return "";
}

function addName(list, name) {
    const n = String(name || "").trim();
    if (n && !list.includes(n)) list.push(n);
}

function blankBill(b, invoice) {
    return {
        key: invoice,
        invoice,
        bookingId: b.id || "",
        customer: b.customer_name || "—",
        mobile: b.customer_mobile || "",
        campus: b.campus || "",
        bookedBy: b.created_by_name || "",
        orderDay: dayKey(b.order_date || b.created_at),
        total: num(b.total_amount),
        due: dueOf(b),
        status: b.status || "",
        cash: 0,
        online: 0,
        qr: 0,
        lines: [],
        qrNames: [],
        qrStaff: [],
        deliverers: [],
        olderDelivery: false,
    };
}

function addLine(bill, line) {
    const sig = `${line.kind}|${line.mode}|${line.day || ""}|${Math.round(line.amount || 0)}`;
    const nth = bill.lines.filter((x) => x.pickSig === sig).length;
    const row = {
        id: `${bill.invoice}|${line.kind}|${line.mode}|${bill.lines.length}`,
        pickSig: sig,
        pickKey: `${bill.invoice}|${sig}|${nth}`,
        invoice: bill.invoice,
        customer: bill.customer,
        bookedBy: bill.bookedBy,
        qrName: "",
        qrFrom: "",
        qrStaff: "",
        alsoQr: "",
        ...line,
    };
    bill.lines.push(row);
    if (row.mode === "cash") bill.cash += row.amount;
    else bill.online += row.amount;
    return row;
}

function touchStaff(map, name) {
    const n = String(name || "").trim() || "Unknown";
    if (!map.has(n)) {
        map.set(n, {
            name: n,
            cash: 0, bookingCash: 0, deliveryCash: 0,
            online: 0, bookingOnline: 0, deliveryOnline: 0,
            qr: 0, qrCount: 0,
            short: 0,
            bookings: 0, deliveries: 0,
        });
    }
    return map.get(n);
}

function touchDay(map, day) {
    if (!map.has(day)) {
        map.set(day, {
            day,
            bookings: 0, advCash: 0, advOnline: 0,
            deliveries: 0, balCash: 0, balOnline: 0,
            qr: 0, qrCount: 0,
        });
    }
    return map.get(day);
}

function touchQr(map, name) {
    const n = String(name || "").trim() || "Unnamed QR";
    if (!map.has(n)) map.set(n, { name: n, total: 0, count: 0, days: new Map(), staff: [] });
    return map.get(n);
}

/**
 * @param {object[]} bookings   maviinci_bookings rows (light read is enough)
 * @param {object[]} qrPayments /api/qr-payments rows
 * @param {{campus?: string, from?: string, to?: string}} drive
 */
const qrStillOpen = (line) => line.mode === "online" && line.qrFrom !== "done" && line.qrFrom !== "picked" && line.qrFrom !== "receipt";

export function buildHisab(bookings, qrPayments, drive = {}, mode = "collection", picks = {}) {
    const campus = drive.campus || "";
    const from = drive.from || "";
    const to = drive.to || "";
    // "collection": payments that landed inside the dates, whichever order they
    // belong to. "orders": every order BOOKED inside the dates, followed from
    // booking to delivery even when the balance came weeks later.
    const orderMode = mode === "orders";

    const list = (bookings || []).filter((b) => b && b.status !== "cancelled" && campusOk(b.campus, campus));
    const byInvoice = new Map();
    list.forEach((b) => {
        const inv = String(b.invoice_no || b.id || "");
        if (inv) byInvoice.set(inv, b);
    });

    const bills = new Map();
    const staff = new Map();
    const days = new Map();
    let olderDeliveries = 0;

    const ensure = (b, invoice) => {
        const key = invoice || String(b.invoice_no || b.id || "");
        if (!bills.has(key)) bills.set(key, blankBill(b, key));
        return bills.get(key);
    };

    list.forEach((b) => {
        const orderDay = dayKey(b.order_date || b.created_at);

        if (inWindow(orderDay, from, to)) {
            const split = advanceSplit(b);
            if (split.cash > 0 || split.online > 0) {
                const bill = ensure(b);
                const who = b.created_by_name || "";
                const s = touchStaff(staff, who);
                const d = touchDay(days, orderDay);
                s.bookings += 1;
                d.bookings += 1;
                const shot = shotRef(b.advance_receipt);
                if (split.cash > 0) {
                    addLine(bill, { kind: "advance", day: orderDay, at: b.created_at || orderDay, mode: "cash", amount: split.cash, holder: who, receipt: shot });
                    s.cash += split.cash; s.bookingCash += split.cash; d.advCash += split.cash;
                }
                if (split.online > 0) {
                    const receiptName = String(b.advance_qr_name || "").trim();
                    addLine(bill, {
                        kind: "advance", day: orderDay, at: b.created_at || orderDay, mode: "online",
                        amount: split.online, holder: who, receipt: shot,
                        ...(receiptName ? { qrName: receiptName, qrFrom: "receipt" } : {}),
                    });
                    s.online += split.online; s.bookingOnline += split.online; d.advOnline += split.online;
                }
            }
        }

        logsOf(b).forEach((lg) => {
            const part = logSplit(lg);
            const day = dayKey(part.at);
            // Orders mode follows the order wherever its payments went; collection
            // mode only wants money that landed inside the dates.
            if (orderMode ? !inWindow(orderDay, from, to) : !inWindow(day, from, to)) return;
            if (part.cash <= 0 && part.online <= 0) return;
            const bill = ensure(b);
            addName(bill.deliverers, part.by);
            if (orderDay && !inWindow(orderDay, from, to)) {
                bill.olderDelivery = true;
                olderDeliveries += 1;
            }
            const s = touchStaff(staff, part.by);
            const d = touchDay(days, day);
            s.deliveries += 1;
            d.deliveries += 1;
            if (part.cash > 0) {
                addLine(bill, { kind: "delivery", day, at: part.at, mode: "cash", amount: part.cash, holder: part.by, receipt: shotRef(part.receipt) });
                s.cash += part.cash; s.deliveryCash += part.cash; d.balCash += part.cash;
            }
            if (part.online > 0) {
                const receiptName = String(part.qrName || "").trim();
                addLine(bill, {
                    kind: "delivery", day, at: part.at, mode: "online", amount: part.online, holder: part.by, receipt: shotRef(part.receipt),
                    ...(receiptName ? { qrName: receiptName, qrFrom: "receipt" } : {}),
                });
                s.online += part.online; s.deliveryOnline += part.online; d.balOnline += part.online;
            }
        });
    });

    // Orders mode lists every booking of the window, paid or not.
    if (orderMode) {
        list.forEach((b) => {
            if (inWindow(dayKey(b.order_date || b.created_at), from, to)) ensure(b);
        });
    }

    // ── QR side ──
    const qrAll = (qrPayments || []).map((q) => ({
        at: q.at || "",
        day: dayKey(q.at),
        clock: clockIST(q.at),
        amount: num(q.amount),
        qrName: q.qr_name || "",
        staffName: q.staff_name || "",
        customer: q.customer_name || "",
        bill: String(q.bill_no || "").trim(),
        campus: q.campus || "",
    }));
    // Orders mode keeps the whole (campus) log as matching material — an order's
    // balance QR can be weeks after the drive. Stats are added after matching,
    // over the entries that turned out to belong to these orders.
    const qrs = qrAll.filter((q) => campusOk(q.campus, campus) && (orderMode || inWindow(q.day, from, to)));

    const byQr = new Map();
    const pools = new Map();
    const loose = [];

    qrs.forEach((q) => {
        q.status = "loose";
        q.matchedInvoice = "";
        if (q.bill && byInvoice.has(q.bill)) {
            ensure(byInvoice.get(q.bill), q.bill);
            if (!pools.has(q.bill)) pools.set(q.bill, []);
            pools.get(q.bill).push(q);
        } else if (q.bill) {
            q.status = "no-such-bill";
        } else {
            loose.push(q);
        }
    });

    const openOnline = (bill, amt) => bill.lines.filter((l) =>
        l.mode === "online" && !l.qrName && (amt == null || Math.abs(l.amount - amt) < 1)
    );

    const dayDistance = (a, b) => {
        if (!a || !b) return 99999;
        const da = Date.parse(`${a}T00:00:00Z`);
        const db = Date.parse(`${b}T00:00:00Z`);
        if (Number.isNaN(da) || Number.isNaN(db)) return 99999;
        return Math.abs(da - db);
    };

    // Same amount can be the advance and the balance. Tie the QR to the
    // payment that happened on the same day, not whichever line was written first.
    const closestOpen = (lines, day) => {
        if (!lines.length) return null;
        return lines.reduce((best, line) => (
            dayDistance(line.day, day) < dayDistance(best.day, day) ? line : best
        ));
    };

    const claim = (line, q, status) => {
        q.status = status;
        q.matchedInvoice = line.invoice;
        line.qrName = q.qrName;
        line.qrStaff = q.staffName;
        line.qrFrom = status === "matched" ? "done" : "done-no-amount";
        line.qrAt = q.at;
    };

    bills.forEach((bill) => {
        const pool = pools.get(bill.invoice) || [];
        // 0. the app already read the name off the screenshot — keep that, and
        //    use the log row only so it is not counted a second time
        pool.forEach((q) => {
            if (q.status !== "loose" || q.amount <= 0) return;
            const named = bill.lines.filter((l) =>
                l.mode === "online" && l.qrFrom === "receipt" && !l.receiptTied && Math.abs(l.amount - q.amount) < 1
            );
            const hit = closestOpen(named, q.day);
            if (!hit) return;
            q.status = "matched";
            q.matchedInvoice = bill.invoice;
            hit.receiptTied = true;
            bill.qr += q.amount;
            addName(bill.qrNames, hit.qrName);
            addName(bill.qrStaff, q.staffName);
        });
        // 1. amount matches an online payment exactly
        pool.forEach((q) => {
            if (q.status !== "loose" || q.amount <= 0) return;
            const hit = closestOpen(openOnline(bill, q.amount), q.day);
            if (!hit) return;
            claim(hit, q, "matched");
            bill.qr += q.amount;
            addName(bill.qrNames, q.qrName);
            addName(bill.qrStaff, q.staffName);
        });
        // 2. Done saved the QR name but no amount
        pool.forEach((q) => {
            if (q.status !== "loose" || q.amount > 0 || !q.qrName) return;
            const left = openOnline(bill, null);
            if (left.length !== 1) return;
            claim(left[0], q, "zero-amount");
            addName(bill.qrNames, q.qrName);
        });
        // 3. amount equals a cash payment — the mode is probably wrong somewhere
        pool.forEach((q) => {
            if (q.status !== "loose" || q.amount <= 0) return;
            const cash = bill.lines.find((l) => l.mode === "cash" && !l.alsoQr && Math.abs(l.amount - q.amount) < 1);
            if (!cash) return;
            q.status = "cash-conflict";
            q.matchedInvoice = bill.invoice;
            cash.alsoQr = q.qrName || "a QR";
            cash.alsoQrStaff = q.staffName;
            addName(bill.qrNames, q.qrName);
        });
        // 4. bill is right but nothing left to cover
        pool.forEach((q) => {
            if (q.status !== "loose") return;
            q.status = "extra";
            q.matchedInvoice = bill.invoice;
        });
    });

    // Done without a bill number is usually a booking advance.
    loose.forEach((q) => {
        if (q.status !== "loose" || q.amount <= 0) return;
        const name = norm(q.customer);
        if (!name) return;
        const cands = [];
        bills.forEach((bill) => {
            if (norm(bill.customer) !== name) return;
            openOnline(bill, q.amount).forEach((line) => cands.push({ bill, line }));
        });
        let pick = cands.length === 1 ? cands[0] : null;
        if (!pick && cands.length > 1) {
            const same = cands.filter((c) => c.line.day === q.day);
            if (same.length === 1) pick = same[0];
        }
        if (!pick) return;
        claim(pick.line, q, "matched");
        pick.bill.qr += q.amount;
        addName(pick.bill.qrNames, q.qrName);
        addName(pick.bill.qrStaff, q.staffName);
    });

    const rows = [...bills.values()].filter((b) => (
        orderMode ? inWindow(b.orderDay, from, to) : b.lines.length
    )).map((b) => {
        const notes = [];
        if (b.due > 0) notes.push("Balance still due");
        if (b.lines.some((l) => l.alsoQr)) notes.push("A cash payment is also on a QR — do not count it twice");
        if (b.olderDelivery) notes.push("Older order, money came in this drive");
        b.lines.sort((x, y) => String(x.at).localeCompare(String(y.at)));
        b.lines.forEach((l) => {
            if (!qrStillOpen(l)) return;
            const chosen = String((picks && picks[l.pickKey]) || "").trim();
            if (!chosen) return;
            l.qrName = chosen;
            l.qrFrom = "picked";
        });
        const short = Math.round(b.lines.filter(qrStillOpen).reduce((a, l) => a + l.amount, 0));
        if (short > 0) notes.unshift("Online with no QR chosen");
        return { ...b, short, notes, collected: Math.round(b.cash + b.online) };
    });

    const byInvoiceNo = (a, b) => String(a.invoice).localeCompare(String(b.invoice), undefined, { numeric: true });
    rows.sort(orderMode ? byInvoiceNo : (a, b) => b.short - a.short || b.due - a.due || byInvoiceNo(a, b));

    // Per-person shortfall is the sum of their own unconfirmed online lines, so the
    // staff column adds up to the same total the bills show.
    rows.forEach((r) => r.lines.forEach((l) => {
        if (qrStillOpen(l)) touchStaff(staff, l.holder).short += l.amount;
    }));

    // QR-side stats. In collection mode every entry of the window counts; in
    // orders mode only the entries that belong to these orders do.
    const shownBill = (inv) => {
        const b = inv && bills.get(inv);
        return !!b && (!orderMode || inWindow(b.orderDay, from, to));
    };
    const finalQrs = orderMode ? qrs.filter((q) => shownBill(q.matchedInvoice || q.bill)) : qrs;

    finalQrs.forEach((q) => {
        const s = touchStaff(staff, q.staffName);
        s.qr += q.amount;
        s.qrCount += 1;
        const d = touchDay(days, q.day);
        d.qr += q.amount;
        d.qrCount += 1;
        const acc = touchQr(byQr, q.qrName);
        acc.total += q.amount;
        acc.count += 1;
        addName(acc.staff, q.staffName);
        const dayCell = acc.days.get(q.day) || { day: q.day, total: 0, count: 0 };
        dayCell.total += q.amount;
        dayCell.count += 1;
        acc.days.set(q.day, dayCell);
    });

    const people = [...staff.values()].map((s) => ({
        ...s,
        short: Math.round(s.short),
        // A person can show more on QR than they collected: the customer scanned a
        // QR in their name while somebody else pressed Done.
        netGap: Math.round(s.qr - s.online),
    })).sort((a, b) => b.short - a.short || b.cash - a.cash || a.name.localeCompare(b.name));

    const qrAccounts = [...byQr.values()].map((a) => ({
        ...a,
        days: [...a.days.values()].sort((x, y) => x.day.localeCompare(y.day)),
    })).sort((a, b) => b.total - a.total);

    const byDay = [...days.values()].map((d) => ({
        ...d,
        cash: d.advCash + d.balCash,
        online: d.advOnline + d.balOnline,
    })).sort((a, b) => a.day.localeCompare(b.day));

    const lines = rows.flatMap((r) => r.lines);
    const shortLines = lines.filter(qrStillOpen)
        .sort((a, b) => b.amount - a.amount);
    const cashConflicts = lines.filter((l) => l.alsoQr);
    const zeroDone = finalQrs.filter((q) => q.amount <= 0);
    const looseQr = finalQrs.filter((q) => q.status === "loose");
    const extraQr = finalQrs.filter((q) => q.status === "extra" || q.status === "no-such-bill");

    const cash = Math.round(lines.reduce((a, l) => a + (l.mode === "cash" ? l.amount : 0), 0));
    const online = Math.round(lines.reduce((a, l) => a + (l.mode === "online" ? l.amount : 0), 0));
    const qrTotal = Math.round(finalQrs.reduce((a, q) => a + q.amount, 0));
    const matchedQr = Math.round(finalQrs.filter((q) => q.status === "matched").reduce((a, q) => a + q.amount, 0));

    return {
        scope: { campus, from, to },
        rows,
        lines,
        people,
        byDay,
        qrAccounts,
        qrEntries: finalQrs.sort((a, b) => String(b.at).localeCompare(String(a.at))),
        qrOldest: qrAll.reduce((min, q) => (q.day && (!min || q.day < min) ? q.day : min), ""),
        qrLoaded: qrAll.length,
        issues: {
            shortLines,
            cashConflicts,
            zeroDone,
            looseQr,
            extraQr,
        },
        olderDeliveries,
        totals: {
            orderValue: Math.round(rows.reduce((a, r) => a + r.total, 0)),
            cash,
            online,
            collected: cash + online,
            qr: qrTotal,
            matchedQr,
            short: Math.round(shortLines.reduce((a, l) => a + l.amount, 0)),
            due: Math.round(rows.reduce((a, r) => a + r.due, 0)),
            bills: rows.length,
            shortBills: rows.filter((r) => r.short > 0).length,
            payments: lines.length,
            qrCount: finalQrs.length,
            looseQr: Math.round(looseQr.reduce((a, q) => a + q.amount, 0)),
        },
    };
}
